import { Word, Wordcloud } from '@buggy/shared'
import { Button, Flex, Skeleton, Spin, Table, TableColumnType } from 'antd'
import { FunctionComponent, ReactNode } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import styled from 'styled-components'
import { nameof } from 'ts-simple-nameof'
import Blur from '@/components/common/blur'
import { Duration } from '@/components/common/duration-select'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import Blocker from '@/components/kol/detail/blocker'
import { initialPostFilterConditions } from '@/components/kol/detail/creative-content-analyses/post-list'
import { NoData } from '@/components/kol/detail/no-data'
import useHashtagAnalyze from '@/components/kol/hooks/use-hashtag-analyze'
import useKolDetailRoute from '@/components/kol/hooks/use-kol-detail-route'
import { demoTermsFrequency } from '@/constants/demo-terms-frequency'
import { useIntl } from '@/i18n/hooks/use-intl'
import { HashTagInfo } from '@/types/api/kols'
import type { KolInfo } from '@/types/schema/kol/kol-info-schema'
import { Platform } from '@/utils/convert-platform'

interface HashtagAnalysesProps {
  duration: Duration
  isFetchingKolInfo: boolean
  isKolInfoLocked: boolean
  kolInfo?: Pick<KolInfo, 'id' | 'dc_id_hashed' | 'name'>
  platform: Platform
  showMockData?: boolean
}

const HashtagAnalyses: FunctionComponent<HashtagAnalysesProps> = ({
  duration,
  isFetchingKolInfo,
  isKolInfoLocked,
  kolInfo,
  platform,
  showMockData,
}) => {
  const { formatMessage, formatNumber } = useIntl()
  const { isPublicKolDetailPage, searchParams, setSearchParams } =
    useKolDetailRoute()

  const { analyze, isFetching } = useHashtagAnalyze({
    kolId: kolInfo?.id,
    kolDcIdHashed: kolInfo?.dc_id_hashed,
    isKolInfoLocked,
    platform: platform.shortcode,
    duration,
  })

  const loading = isFetchingKolInfo || isFetching
  const data = isKolInfoLocked
    ? demoTermsFrequency
        .map((item): HashTagInfo => {
          return {
            platformType: platform.shortcode,
            hashtag: item.Text,
            count: item.Value,
            engagementRate: item.Value / 100,
          }
        })
        .sort((prev, next) => next.count - prev.count)
    : analyze?.sort((prev, next) => next.count - prev.count)
  const columns: TableColumnType<HashTagInfo>[] = [
    {
      title: 'No.',
      width: 50,
      key: 'rank',
      render: (_text, _post, index) => <Rank>#{index + 1}</Rank>,
    },
    {
      title: 'Hashtag',
      ellipsis: true,
      dataIndex: nameof<HashTagInfo>((s) => s.hashtag),
      render: (hashtag: HashTagInfo['hashtag']): ReactNode => {
        const text = `#${hashtag}`
        return (
          <HashtagButton
            type='link'
            onClick={
              isKolInfoLocked
                ? undefined
                : async (): Promise<void> => handleClickHashtag(text)
            }
          >
            {text}
          </HashtagButton>
        )
      },
    },
    {
      width: 120,
      ellipsis: true,
      title: formatMessage({ id: 'kol:frequency_count' }),
      dataIndex: nameof<HashTagInfo>((s) => s.count),
      render: (count: HashTagInfo['count']) => <NumberText>{count}</NumberText>,
    },
    {
      width: 120,
      ellipsis: true,
      title: formatMessage({ id: 'project:metric_engagement_rate' }),
      dataIndex: nameof<HashTagInfo>((s) => s.engagementRate),
      render: (engagementRate: HashTagInfo['count']) => (
        <NumberText>
          {formatNumber(engagementRate, { isPercentageDisplay: true })}
        </NumberText>
      ),
    },
  ]

  const handleClickHashtag = async (hashtag: string): Promise<void> => {
    await setSearchParams(
      {
        ...searchParams.data,
        ...initialPostFilterConditions,
        keyword: hashtag,
        postListPlatform: platform.shortcode,
      },
      'postList',
    )
  }

  return (
    <KolDetailDataBlock
      colSpan={24}
      header={{
        title: formatMessage(
          {
            id: 'kol:hashtag_analyses_data_block_title',
          },
          { platformTitle: platform.title },
        ),
      }}
      kolId={kolInfo?.id}
      kolMetrics='Hashtag Analysis'
      kolName={kolInfo?.name}
      platform={platform.shortcode}
    >
      <Flex align='center' gap={30}>
        <Item>
          {((): ReactNode => {
            if (loading) {
              return (
                <Flex
                  align='center'
                  justify='center'
                  style={{ minHeight: 200 }}
                >
                  <Spin />
                </Flex>
              )
            }

            if (!data?.length) {
              return <NoData />
            }

            return (
              <Spin indicator={<Blocker />} spinning={isPublicKolDetailPage}>
                <AutoSizer disableHeight>
                  {({ width }): ReactNode => {
                    return (
                      <Blur activate={isKolInfoLocked && !showMockData}>
                        <Wordcloud
                          height={230}
                          width={width}
                          words={data.map((hashtag): Word => {
                            return {
                              text: `#${hashtag.hashtag}`,
                              value: hashtag.count,
                            }
                          })}
                          onClick={
                            isKolInfoLocked ? undefined : handleClickHashtag
                          }
                        />
                      </Blur>
                    )
                  }}
                </AutoSizer>
              </Spin>
            )
          })()}
        </Item>
        {((): ReactNode => {
          if (loading) {
            return (
              <Item>
                <Skeleton
                  active
                  paragraph={{ rows: 3, width: '20%' }}
                  title={false}
                />
              </Item>
            )
          }

          if (!data?.length) {
            return null
          }

          return (
            <Item>
              <Blur activate={isKolInfoLocked && !showMockData}>
                <StyledTable
                  columns={columns}
                  dataSource={data.slice(0, 5)}
                  pagination={false}
                />
              </Blur>
            </Item>
          )
        })()}
      </Flex>
    </KolDetailDataBlock>
  )
}

const Rank = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
`

const HashtagButton = styled(Button)`
  padding: 0;
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
  cursor: ${({ onClick }): string => (onClick ? 'pointer' : 'default')};
  color: ${({ theme }): string => theme.colors.brand.secondary};

  &:hover {
    color: ${({ theme }): string =>
      theme.colors.brand.secondaryHover} !important;
  }

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
  }
`

const NumberText = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
`

const Item = styled.div`
  flex: 1;
`

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    border: none;
    color: ${({ theme }): string => theme.colors.text.secondary};
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
  }

  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    padding: 0 0 0 16px;

    &:first-child {
      padding-left: 20px;
    }

    &:last-child {
      padding-right: 20px;
    }
  }

  .ant-table-row {
    height: 40px;
  }

  .ant-table-thead
    > tr
    > th:not(:last-child):not(.ant-table-selection-column):not(
      .ant-table-row-expand-icon-cell
    ):not([colspan])::before {
    display: none;
  }
`

export default HashtagAnalyses
